import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';
import WizardSteps from '../components/WizardSteps';
import WizardStepLocation from '../components/WizardStepLocation';
import WizardStepChargers from '../components/WizardStepChargers';
import WizardStepConnectors from '../components/WizardStepConnectors';
import WizardStepPrice from '../components/WizardStepPrice';
import WizardStepReview from '../components/WizardStepReview';
import BulkUploadReview from '../components/BulkUploadReview';
import { findUnresolvedConflicts, findCoverageGaps } from '../utils/scheduleConflict';

const API = 'https://api.cpms-srlkevcharge.com';

const STEP_LABELS = ['Create Location', 'Add Chargers', 'Add Connectors', 'Add Price', 'Review & Register'];

const IconZap = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IconDownload = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IconUpload = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export default function AddCharger() {
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const [mode, setMode] = useState('choose');

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [tariffLookup, setTariffLookup] = useState({});

  const [uploadStage, setUploadStage] = useState('none');
  const [bulkData, setBulkData] = useState(null);
  const [registering, setRegistering] = useState(false);
  const fileInputRef = React.useRef(null);

  const [organisationMode, setOrganisationMode] = useState('existing');
  const [organisationId, setOrganisationId] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [locationSelectedFields, setLocationSelectedFields] = useState([]);
  const [locationFormData, setLocationFormData] = useState({});
  const [locationErrors, setLocationErrors] = useState({});

  const [chargers, setChargers] = useState([]);
  const [connectors, setConnectors] = useState([]);

  const [schedules, setSchedules] = useState([]);
  const [unresolvedConflicts, setUnresolvedConflicts] = useState([]);
  const [gapWarnings, setGapWarnings] = useState([]);
  const [gapWarningAcknowledged, setGapWarningAcknowledged] = useState(false);

  React.useEffect(() => {
    axios.get(`${API}/organisations/`)
      .then(res => setOrganisations(res.data))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    axios.get(`${API}/tariffs/`)
      .then(res => {
        const lookup = {};
        res.data.forEach(t => { lookup[t.id] = t.name; });
        setTariffLookup(lookup);
      })
      .catch(() => {});
  }, []);

  const validateStep1 = () => {
    const errs = {};
    if (organisationMode === 'existing' && !organisationId) {
      errs.organisation = 'Please select an organisation';
    }
    if (organisationMode === 'new' && !organisationName.trim()) {
      errs.organisation = 'Please enter a name for the new organisation';
    }
    if (!locationFormData.station_name?.trim()) {
      errs.station_name = 'Station name is required';
    }
    setLocationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    if (chargers.length === 0) {
      showToast('Add at least one charger to continue', 'error');
      return false;
    }
    let valid = true;
    setChargers(prev => prev.map(c => {
      const errs = {};
      if (!c.formData.ocpp_id?.trim()) errs.ocpp_id = 'EVSE ID is required';
      if (Object.keys(errs).length > 0) valid = false;
      return { ...c, errors: errs };
    }));
    return valid;
  };

  const validateStep3 = () => {
    if (connectors.length === 0) {
      showToast('Add at least one connector to continue', 'error');
      return false;
    }
    let valid = true;
    setConnectors(prev => prev.map(c => {
      const errs = {};
      if (!c.formData.connector_type?.trim()) errs.connector_type = 'Connector type is required';
      if (!c.formData.max_ampere?.trim()) errs.max_ampere = 'Max ampere is required';
      if (Object.keys(errs).length > 0) valid = false;
      return { ...c, errors: errs };
    }));
    return valid;
  };

  const validateStep4 = () => {
    const schedulesWithTariff = schedules.filter(s => s.tariff_id);
    const gaps = schedulesWithTariff.length > 0 ? findCoverageGaps(schedulesWithTariff) : [];
    setGapWarnings(gaps);

    let conflicts = [];
    if (schedulesWithTariff.length >= 2) {
      conflicts = findUnresolvedConflicts(schedulesWithTariff, tariffLookup);
      setUnresolvedConflicts(conflicts.map(c => ({
        ...c,
        tempIdA: schedulesWithTariff[c.indexA].tempId,
        tempIdB: schedulesWithTariff[c.indexB].tempId,
      })));
    } else {
      setUnresolvedConflicts([]);
    }

    if (conflicts.length > 0) return false;

    // If there are gap warnings the operator has not yet acknowledged,
    // stop once so they see the warning before proceeding.
    if (gaps.length > 0 && !gapWarningAcknowledged) {
      setGapWarningAcknowledged(true);
      return false;
    }

    return true;
  };

  const resolveConflict = (tempIdWinner, tempIdLoser) => {
    setSchedules(prev => {
      const updated = prev.map(s =>
        s.tempId === tempIdWinner ? { ...s, priority: (s.priority || 0) + 1 } : s
      );

      // Immediately re-check conflicts against the updated schedules so the
      // resolved collision box clears right away.
      const schedulesWithTariff = updated.filter(s => s.tariff_id);
      if (schedulesWithTariff.length >= 2) {
        const conflicts = findUnresolvedConflicts(schedulesWithTariff, tariffLookup);
        setUnresolvedConflicts(conflicts.map(c => ({
          ...c,
          tempIdA: schedulesWithTariff[c.indexA].tempId,
          tempIdB: schedulesWithTariff[c.indexB].tempId,
        })));
      } else {
        setUnresolvedConflicts([]);
      }

      return updated;
    });
    setGapWarningAcknowledged(false);
  };

  const goNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    if (currentStep === 4) {
      const passed = validateStep4();
      // Scroll to top so any collision box or gap warning is visible,
      // whether validation passed (to show gap warnings) or failed
      // (to show the collision box), since the operator may be scrolled
      // down while adding tariff cards.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (!passed) return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleRegister = async () => {
    setSubmitting(true);

    const payload = {
      location: { ...locationFormData },
      chargers: chargers.map(charger => {
        const chargerConnectors = connectors.filter(c => c.chargerTempId === charger.tempId);
        return {
          ...charger.formData,
          connectors: chargerConnectors.map((connector, index) => ({
            connector_number: index + 1,
            ...connector.formData
          }))
        };
      }),
      tariff_schedules: schedules
        .filter(s => s.tariff_id)
        .map(s => ({
          tariff_id: Number(s.tariff_id),
          start_time: s.start_time || null,
          end_time: s.end_time || null,
          days_of_week: s.days_of_week || null,
          effective_start_date: s.effective_start_date || null,
          effective_end_date: s.effective_end_date || null,
          priority: s.priority || 0,
        }))
    };

    if (organisationMode === 'existing') {
      payload.organisation_id = Number(organisationId);
    } else {
      payload.organisation_name = organisationName.trim();
    }

    try {
      const res = await axios.post(`${API}/locations/wizard-register`, payload);
      showToast(`${res.data.station_name} registered successfully with ${chargers.length} charger(s)`);
      navigate('/');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to register EVSE', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/bulk-upload/template`, '_blank');
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setMode('bulk');

    const fd = new FormData();
    fd.append('file', file);

    setUploadStage('reading');
    try {
      const res = await axios.post(`${API}/bulk-upload/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkData(res.data);
      setUploadStage('reviewed');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to read the uploaded file', 'error');
      setUploadStage('none');
      setMode('choose');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpdateBulkLocation = (index, updatedLocation) => {
    setBulkData(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) => i === index ? updatedLocation : loc)
    }));
  };

  const handleRemoveBulkLocation = (index) => {
    setBulkData(prev => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index),
    }));
  };

  const isBulkLocationValid = (loc) => {
    const totalConnectors = loc.chargers.reduce((sum, c) => sum + c.connectors.length, 0);
    const validConnectors = loc.chargers.reduce((sum, c) => sum + c.valid_connector_count, 0);
    return loc.errors.length === 0 && validConnectors === totalConnectors && totalConnectors > 0
      && loc.organisation_name && loc.location_fields.station_name;
  };

  const handleBulkRegister = async () => {
    const validLocations = bulkData.locations.filter(isBulkLocationValid);
    if (validLocations.length === 0) {
      showToast('No fully valid locations to register', 'error');
      return;
    }

    const payload = {
      locations: validLocations.map(loc => ({
        organisation_name: loc.organisation_name,
        tariff_schedules: (loc.tariff_matches || [])
          .filter(m => m.found)
          .map(m => ({
            tariff_id: m.tariff_id,
            start_time: m.start_time || null,
            end_time: m.end_time || null,
            days_of_week: m.days_of_week || null,
            effective_start_date: m.effective_start_date || null,
            effective_end_date: m.effective_end_date || null,
            priority: m.priority || 0,
          })),
        ...loc.location_fields,
        chargers: loc.chargers.map(charger => ({
          ...charger.charger_fields,
          connectors: charger.connectors.map(c => c.connector_fields)
        }))
      }))
    };

    setRegistering(true);
    try {
      const res = await axios.post(`${API}/bulk-upload/register`, payload);
      showToast(res.data.message);
      navigate('/');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Bulk registration failed', 'error');
    } finally {
      setRegistering(false);
    }
  };

  const cancelBulkUpload = () => {
    setMode('choose');
    setUploadStage('none');
    setBulkData(null);
  };

  const validBulkCount = bulkData ? bulkData.locations.filter(isBulkLocationValid).length : 0;

  return (
    <>
      <button className="back-link" onClick={() => mode === 'choose' ? navigate('/') : setMode('choose')}>
        ← {mode === 'choose' ? 'Back to Overview' : 'Back to Options'}
      </button>

      <div className="page-header">
        <h1>Add EVSE</h1>
        <p>Register a new charging station, its chargers, and connectors</p>
      </div>

      {mode === 'choose' && (
        <>
          <div className="evse-import-banner">
            <div className="evse-import-banner-text">
              <h3>Register using a spreadsheet</h3>
              <p>Download the template, fill it in, and upload it. Everything is validated before anything is registered, whether you are adding one station or many.</p>
            </div>
            <div className="evse-import-actions">
              <button className="btn btn-outline-green" onClick={handleDownloadTemplate}>
                <IconDownload /> Download Template
              </button>
              <button className="btn btn-green" onClick={() => fileInputRef.current?.click()}>
                <IconUpload /> Upload Completed File
              </button>
            </div>
          </div>

          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
            <h3 style={{ marginBottom: 8 }}>Or register manually</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: 13.5 }}>
              Step through creating a location, its chargers, and connectors one at a time.
            </p>
            <button className="btn btn-green" onClick={() => setMode('wizard')}>
              Start Manual Registration
            </button>
          </div>
        </>
      )}

      {mode === 'bulk' && uploadStage === 'reading' && (
        <div className="card empty-state">
          <h3>Reading your file...</h3>
          <p>Grouping rows into locations, chargers, and connectors.</p>
        </div>
      )}

      {mode === 'bulk' && uploadStage === 'reviewed' && bulkData && (
        <>
          <div className="preview-summary-bar">
            <div className="preview-summary-text">
              <span className="good">{validBulkCount} location{validBulkCount !== 1 ? 's' : ''} ready to register</span>
              {bulkData.locations.length - validBulkCount > 0 && (
                <> · <span className="bad">{bulkData.locations.length - validBulkCount} need attention</span></>
              )}
              {' '}from {bulkData.total_rows} row{bulkData.total_rows !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={cancelBulkUpload}>Cancel</button>
              <button
                className="btn btn-green"
                onClick={handleBulkRegister}
                disabled={registering || validBulkCount === 0}
              >
                {registering ? 'Registering...' : `Register ${validBulkCount} Location${validBulkCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>

          {bulkData.ungrouped_errors.length > 0 && (
            <div className="bulk-errors-inline" style={{ marginBottom: 16 }}>
              <strong>Rows that could not be grouped:</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {bulkData.ungrouped_errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}

          <BulkUploadReview
            locations={bulkData.locations}
            onUpdateLocation={handleUpdateBulkLocation}
            onRemoveLocation={handleRemoveBulkLocation}
          />
        </>
      )}

      {mode === 'wizard' && (
        <>
          <WizardSteps steps={STEP_LABELS} currentStep={currentStep} />

          {currentStep === 1 && (
            <WizardStepLocation
              organisationMode={organisationMode} setOrganisationMode={setOrganisationMode}
              organisationId={organisationId} setOrganisationId={setOrganisationId}
              organisationName={organisationName} setOrganisationName={setOrganisationName}
              selectedFields={locationSelectedFields} setSelectedFields={setLocationSelectedFields}
              formData={locationFormData} setFormData={setLocationFormData}
              errors={locationErrors} setErrors={setLocationErrors}
            />
          )}

          {currentStep === 2 && (
            <WizardStepChargers chargers={chargers} setChargers={setChargers} />
          )}

          {currentStep === 3 && (
            <WizardStepConnectors chargers={chargers} connectors={connectors} setConnectors={setConnectors} />
          )}

          {currentStep === 4 && (
            <WizardStepPrice
              schedules={schedules}
              setSchedules={(updater) => {
                setSchedules(updater);
                setGapWarningAcknowledged(false);
              }}
              unresolvedConflicts={unresolvedConflicts}
              onResolveConflict={resolveConflict}
              gapWarnings={gapWarnings}
              tariffLookup={tariffLookup}
            />
          )}

          {currentStep === 5 && (
            <WizardStepReview
              organisationMode={organisationMode}
              organisationId={organisationId}
              organisationName={organisationName}
              organisations={organisations}
              locationFormData={locationFormData}
              chargers={chargers}
              connectors={connectors}
              schedules={schedules}
            />
          )}

          <div className="wizard-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={currentStep === 1 ? () => setMode('choose') : goBack}
            >
              {currentStep === 1 ? 'Cancel' : '← Back'}
            </button>

            {currentStep < 5 ? (
              <button type="button" className="btn btn-green" onClick={goNext}>
                {currentStep === 4
                  ? (gapWarnings.length > 0 && gapWarningAcknowledged ? 'Proceed Anyway →' : 'Skip / Proceed →')
                  : 'Proceed to Next Step →'}
              </button>
            ) : (
              <button type="button" className="btn btn-green" onClick={handleRegister} disabled={submitting}>
                {submitting ? 'Registering...' : 'Create New Charging Station'}
              </button>
            )}
          </div>
        </>
      )}

      <input
        type="file" accept=".xlsx,.xls" ref={fileInputRef}
        style={{ display: 'none' }} onChange={handleFileSelected}
      />
    </>
  );
}